/**
 * Webapp component.
 *
 * Copyright © Ircam Amplify 2024-2026
 */
import amplifyConfig from '@/amplify.config'
import MainContainer from '@/components/Maincontainer'
import MyAccount from '@/components/MyAccount'
import SideBar from '@/components/Sidebar'
import TopBar from '@/components/Topbar'
import TwoPaneLayout from '@/components/TwoPaneLayout'
import RequiresAuth from '@/guards/RequiresAuth'
import RequiresNoAuth from '@/guards/RequiresNoAuth'
import '@fontsource/barlow/400.css'
import '@fontsource/barlow/500.css'
import '@fontsource/barlow/600.css'

import {
  APICredentials,
  Billing,
  ChangePassword,
  Checkout,
  ConfirmAccount,
  CreateJob,
  CreateWebhook,
  ForgotPassword,
  ForgotPasswordConfirmation,
  HomePage,
  JobsHome,
  Login,
  NewApiCredentials,
  ResendConfirmationEmail,
  SelectTier,
  Settings,
  Signup,
  TaskCreationPage,
  WebhookLogs,
  Webhooks,
} from '@/pages'
import { CheckoutResult } from '@/pages/CheckoutResult'
import {
  getUserState,
  persistor,
  setSideBarCollapsed,
  store,
  useSelector,
} from '@/store'
import { Amplify } from 'aws-amplify'
import { useEffect } from 'react'
import 'react-loading-skeleton/dist/skeleton.css'
import { Provider } from 'react-redux'
import { Outlet, Route, Routes } from 'react-router'
import { useLocation } from 'react-router-dom'
import { PersistGate } from 'redux-persist/integration/react'

Amplify.configure(amplifyConfig)
console.log(`App version: ${import.meta.env.VITE_APP_VERSION}`)
console.log('Locale:', navigator.language)

const HomeLayout = () => {
  const { userInfo } = useSelector(getUserState)
  return (
    <>
      <TopBar
        title={`Welcome ${userInfo?.value?.user_firstname ?? ''}`}
        description=""
      />
      <HomePage />
    </>
  )
}

const App = () => {
  const location = useLocation()

  useEffect(() => {
    const routeNames: any = {
      '/api-credentials': 'API Credentials',
      '/api-credentials/create': 'Create API Credentials',
      '/usage': 'Usage',
      '/change-password': 'Change password',
      '/confirm': 'Confirm Account',
      '/forgot-password': 'Forgot Password',
      '/forgot-password-confirmation': 'New Password Confirmation',
      '/': 'Home Page',
      '/job-creation': 'Create a new Task',
      '/jobs': 'Tasks History',
      '/login': 'Log in',
      '/resend-activation-link': 'E-mail Confirmation',
      '/tierselection': 'Subscription Selection',
      '/settings': 'Settings',
      '/signup': 'Sign up',
      '/thank-you': 'Thank you',
      '/webhooks/create': 'Create a Webhook',
      '/webhooks': 'Webhooks',
      '/my-account': 'My account',
    }

    if (
      routeNames?.[location?.pathname] !== undefined &&
      document.title !== routeNames?.[location?.pathname]
    ) {
      document.title = `${routeNames?.[location?.pathname]} | ${window.location.host}`
    } else if (
      (location?.pathname ?? '').includes('/webhooks') &&
      (location?.pathname ?? '').includes('/logs') &&
      document.title !== 'Webhooks Logs'
    ) {
      document.title = `Webhooks Logs | ${window.location.host}`
    }
  }, [location?.pathname])

  useEffect(() => {
    const handleResize = () => {
      const width =
        window?.innerWidth ||
        document?.documentElement?.clientWidth ||
        document?.body?.clientWidth ||
        0
      const sideBarCollapsed = store.getState().layout.sideBarCollapsed
      if (width < 640 && !sideBarCollapsed) {
        store.dispatch(setSideBarCollapsed(true))
      }
      if (width >= 640 && sideBarCollapsed) {
        store.dispatch(setSideBarCollapsed(false))
      }
    }
    window.addEventListener('resize', handleResize)
    handleResize()
    return () => {
      window.addEventListener('resize', handleResize)
    }
  }, [])

  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <Routes>
          <Route
            path="/login"
            element={
              <RequiresNoAuth>
                <TwoPaneLayout>
                  <Login />
                </TwoPaneLayout>
              </RequiresNoAuth>
            }
          />
          <Route
            path="/confirm"
            element={
              <RequiresNoAuth>
                <ConfirmAccount />
              </RequiresNoAuth>
            }
          />
          <Route
            path="/signup"
            element={
              <RequiresNoAuth>
                <TwoPaneLayout>
                  <Signup />
                </TwoPaneLayout>
              </RequiresNoAuth>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <RequiresNoAuth>
                <TwoPaneLayout>
                  <ForgotPassword />
                </TwoPaneLayout>
              </RequiresNoAuth>
            }
          />
          <Route
            path="/forgot-password-confirmation"
            element={
              <RequiresNoAuth>
                <TwoPaneLayout>
                  <ForgotPasswordConfirmation />
                </TwoPaneLayout>
              </RequiresNoAuth>
            }
          />
          <Route
            path="/resend-activation-link"
            element={
              <RequiresNoAuth>
                <TwoPaneLayout>
                  <ResendConfirmationEmail />
                </TwoPaneLayout>
              </RequiresNoAuth>
            }
          />
          <Route
            path="tierselection"
            element={
              <RequiresAuth>
                <SelectTier />
              </RequiresAuth>
            }
          />

          <Route
            path="/change-password"
            element={
              <RequiresAuth>
                <TwoPaneLayout>
                  <ChangePassword />
                </TwoPaneLayout>
              </RequiresAuth>
            }
          />

          <Route
            path="checkout"
            element={
              <RequiresAuth>
                <Checkout />
              </RequiresAuth>
            }
          />
          <Route
            path="payment-result"
            element={
              <RequiresAuth>
                <CheckoutResult />
              </RequiresAuth>
            }
          />
          <Route
            path="/"
            element={
              <RequiresAuth>
                <>
                  <SideBar />
                  <MainContainer>
                    <Outlet />
                  </MainContainer>
                </>
              </RequiresAuth>
            }
          >
            <Route path="" element={<HomeLayout />} />
            <Route
              path="settings"
              element={
                <>
                  <TopBar title="Settings" description="Set your alerts" />
                  <Settings />
                </>
              }
            />
            <Route
              path="usage"
              element={
                <>
                  <TopBar title={'Usage'} description="" />
                  <Billing />
                </>
              }
            />
            <Route
              path="api-credentials"
              element={
                <>
                  <TopBar
                    title="API Credentials"
                    description="Manage your API credentials according to your own security policy."
                  />
                  <APICredentials />
                </>
              }
            />
            <Route
              path="api-credentials/create"
              element={
                <>
                  <TopBar
                    title="API Credentials"
                    description="Manage your API credentials according to your own security policy."
                  />
                  <NewApiCredentials />
                </>
              }
            />
            <Route
              path="my-account"
              element={
                <>
                  <MyAccount />
                </>
              }
            />
            <Route
              path="send-activation-link"
              element={
                <TwoPaneLayout>
                  <ResendConfirmationEmail />
                </TwoPaneLayout>
              }
            />
            <Route path="jobs" element={<JobsHome />} />
            <Route path="job-creation" element={<CreateJob />} />
            <Route
              path="job-creation/:moduleName"
              element={<TaskCreationPage />}
            />

            <Route path="webhooks" element={<Webhooks />} />
            <Route
              path="webhooks/create"
              element={
                <>
                  <TopBar
                    title="Webhooks"
                    description="Manage your webhooks."
                  />
                  <CreateWebhook />
                </>
              }
            />
            <Route path="webhooks/:webhookId/logs" element={<WebhookLogs />} />
            <Route
              path="*"
              element={<div>The page you are looking for does not exist.</div>}
            />
          </Route>
        </Routes>
      </PersistGate>
    </Provider>
  )
}

export default App
